import AOS from 'aos';

const SELECTOR_LEFT = '[data-our-process-left]';

/**
 * Our Process Carousel - Controls carousel navigation and dynamic content updates
 */
export class OurProcessCarousel {
  private carousel: HTMLElement;
  private slide: HTMLElement;
  private prevButton: HTMLButtonElement;
  private nextButton: HTMLButtonElement;
  private progressBar: HTMLElement;
  private imageTrack: HTMLElement;
  private prevSlide: HTMLElement;
  private currentSlide: HTMLElement;
  private nextSlide: HTMLElement;
  private currentIndex: number = 0;
  private totalSlides: number;
  private carouselData: any[] = [];
  private isAnimating: boolean = false;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private touchEndX: number = 0;
  private minSwipeDistance: number = 50;
  private floatingImage: HTMLElement | null = null;
  private lastScrollY: number = 0;
  
  // Tablet-specific properties
  private tabletScrollWrapper: HTMLElement | null = null;
  private tabletScrollContainer: HTMLElement | null = null;
  private tabletProgressBar: HTMLElement | null = null;
  private isTablet: boolean = false;

  constructor(carousel: HTMLElement) {
    this.carousel = carousel;
    this.slide = carousel.querySelector('.our-process-carousel__slide') as HTMLElement;
    this.prevButton = carousel.querySelector('.our-process-carousel__nav-button--prev') as HTMLButtonElement;
    this.nextButton = carousel.querySelector('.our-process-carousel__nav-button--next') as HTMLButtonElement;
    this.progressBar = carousel.querySelector('.our-process-carousel__slide--desktop-mobile .our-process-carousel__progressbar-fill') as HTMLElement;
    this.imageTrack = carousel.querySelector('.our-process-carousel__image-track') as HTMLElement;
    this.prevSlide = carousel.querySelector('.our-process-carousel__image-slide--prev') as HTMLElement;
    this.currentSlide = carousel.querySelector('.our-process-carousel__image-slide--current') as HTMLElement;
    this.nextSlide = carousel.querySelector('.our-process-carousel__image-slide--next') as HTMLElement;
    this.floatingImage = carousel.querySelector('.our-process-carousel__floating-image') as HTMLElement;
    
    // Tablet-specific elements
    this.tabletScrollWrapper = carousel.querySelector('.our-process-carousel__tablet-scroll-wrapper') as HTMLElement;
    this.tabletScrollContainer = carousel.querySelector('.our-process-carousel__tablet-scroll-container') as HTMLElement;
    this.tabletProgressBar = carousel.querySelector('.our-process-carousel__tablet .our-process-carousel__progressbar-fill') as HTMLElement;
    
    // Get data from data attribute
    const dataString = this.carousel.getAttribute('data-carousel-data');
    
    if (dataString) {
      try {
        this.carouselData = JSON.parse(dataString);
        this.totalSlides = this.carouselData.length;
        console.log('Carousel data loaded - Total slides:', this.totalSlides);
      } catch (e) {
        console.error('Error parsing carousel data:', e, 'Data string:', dataString);
        this.totalSlides = parseInt(this.carousel.getAttribute('data-carousel-count') || '0');
      }
    } else {
      console.error('No carousel data found in data-carousel-data attribute');
    }

    this.init();
  }

  private init(): void {
    // Check if we're on tablet viewport
    this.checkViewport();
    
    window.addEventListener('resize', () => this.handleResize());
    
    if (this.totalSlides <= 1) {
      // Hide navigation if only one slide
      const navigation = this.carousel.querySelector('.our-process-carousel__navigation');
      if (navigation) {
        (navigation as HTMLElement).style.display = 'none';
      }
      const tabletNavigation = this.carousel.querySelector('.our-process-carousel__tablet-navigation');
      if (tabletNavigation) {
        (tabletNavigation as HTMLElement).style.display = 'none';
      }
      if (!this.isTablet) this.updateFloatingImageTop();
      return;
    }

    if (this.isTablet) {
      this.initTabletCarousel();
    } else {
      this.bindEvents();
      this.updateNavigation();
      this.initializeImages();
      this.bindScrollEffects();
      this.updateFloatingImageTop();
    }
    
    AOS.init({ duration: 800, easing: 'ease-out', once: true });
  }

  private checkViewport(): void {
    const width = window.innerWidth;
    this.isTablet = width >= 750 && width < 1200;
  }

  private handleResize(): void {
    const wasTablet = this.isTablet;
    this.checkViewport();
    
    // If viewport changed from/to tablet, reinitialize
    if (wasTablet !== this.isTablet) {
      if (this.isTablet) {
        this.initTabletCarousel();
      } else {
        this.updateNavigation();
      }
    } else if (!this.isTablet) {
      this.updateFloatingImageTop();
    }
  }

  
  private updateFloatingImageTop(): void {
    const leftEl = this.slide?.querySelector(SELECTOR_LEFT) as HTMLElement;
    if (!leftEl || !this.slide) return;

    const rect = leftEl.getBoundingClientRect();
    const style = window.getComputedStyle(leftEl);
    const marginTop = parseFloat(style.marginTop) || 0;
    const marginBottom = parseFloat(style.marginBottom) || 0;
    const measured = rect.height + marginTop + marginBottom;
    const gapPx = window.innerWidth >= 1650 ? 64 : 50; 
    const totalHeightPx = measured + gapPx;

    this.slide.style.setProperty('--left-height-total', `${totalHeightPx}px`);
  }

  private initTabletCarousel(): void {
    if (!this.tabletScrollWrapper || !this.tabletScrollContainer) return;
    
    // Bind scroll event for progress bar update
    this.tabletScrollWrapper.addEventListener('scroll', () => this.updateTabletProgress(), { passive: true });
    
    // Initialize progress bar
    this.updateTabletProgress();
  }

  private updateTabletProgress(): void {
    if (!this.tabletScrollWrapper || !this.tabletScrollContainer || !this.tabletProgressBar) return;
    
    // Calculate which slide is currently visible
    const scrollLeft = this.tabletScrollWrapper.scrollLeft;
    const slideWidth = this.tabletScrollWrapper.clientWidth * 0.7; // 70vw per slide
    const gap = 24; // 1.5rem gap in pixels (approximate)
    
    // Estimate current slide index based on scroll position
    const estimatedIndex = Math.round(scrollLeft / (slideWidth + gap));
    const currentSlideIndex = Math.max(0, Math.min(estimatedIndex, this.totalSlides - 1));
    
    // Calculate progress based on slide index (1-based for user)
    const progress = ((currentSlideIndex + 1) / this.totalSlides) * 100;
    this.tabletProgressBar.style.width = `${Math.min(progress, 100)}%`;
  }

  private bindEvents(): void {
    // Previous/Next button clicks
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.goToPrevSlide());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goToNextSlide());
    }

    // Keyboard navigation
    this.carousel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.goToPrevSlide();
      } else if (e.key === 'ArrowRight') {
        this.goToNextSlide();
      }
    });

    // Touch/swipe navigation for mobile
    this.bindTouchEvents();

    // Optional: Auto-play functionality
    // this.startAutoPlay();
  }

  private bindScrollEffects(): void {
    if (!this.floatingImage) return;
    
    this.lastScrollY = window.scrollY;
    
    // Bind scroll event with throttling
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  private handleScroll(): void {
    if (!this.floatingImage) return;

    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - this.lastScrollY;
    
    // Calculate parallax offset (very subtle movement)
    const parallaxSpeed = 0.1; // Adjust this for more/less movement
    const maxOffset = 20; // Maximum pixels to move
    
    // Get current transform or initialize
    const currentTransform = this.floatingImage.style.transform || '';
    const currentY = this.extractTranslateY(currentTransform);
    
    // Calculate new Y position
    let newY = currentY - (scrollDelta * parallaxSpeed);
    
    // Clamp to max offset range
    newY = Math.max(-maxOffset, Math.min(maxOffset, newY));
    
    // Apply transform while preserving other transforms
    const otherTransforms = currentTransform.replace(/translateY\([^)]*\)/g, '').trim();
    this.floatingImage.style.transform = `translateY(${newY}px) ${otherTransforms}`;
    
    this.lastScrollY = currentScrollY;
  }

  private extractTranslateY(transform: string): number {
    const match = transform.match(/translateY\(([^)]+)\)/);
    if (match) {
      return parseFloat(match[1]) || 0;
    }
    return 0;
  }

  private bindTouchEvents(): void {
    // Touch start
    this.carousel.addEventListener('touchstart', (e) => {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    }, { passive: true });

    // Touch move - prevent horizontal scrolling during swipe
    this.carousel.addEventListener('touchmove', (e) => {
      const touchMoveX = e.touches[0].clientX;
      const touchMoveY = e.touches[0].clientY;
      const deltaX = Math.abs(touchMoveX - this.touchStartX);
      const deltaY = Math.abs(touchMoveY - this.touchStartY);
      
      // If horizontal movement is greater than vertical, prevent default to stop page scroll
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }, { passive: false });

    // Touch end
    this.carousel.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].clientX;
      this.handleSwipe();
    }, { passive: true });
  }

  private handleSwipe(): void {
    if (this.isAnimating) return;

    const swipeDistance = this.touchStartX - this.touchEndX;
    const absSwipeDistance = Math.abs(swipeDistance);

    // Check if swipe distance is sufficient
    if (absSwipeDistance < this.minSwipeDistance) return;

    // Determine swipe direction
    if (swipeDistance > 0) {
      // Swiped left - go to next slide
      this.goToNextSlide();
    } else {
      // Swiped right - go to previous slide
      this.goToPrevSlide();
    }
  }

  private goToSlide(index: number, direction: 'next' | 'prev' = 'next'): void {
    if (index < 0 || index >= this.totalSlides || index === this.currentIndex || this.isAnimating) {
      return;
    }

    this.isAnimating = true;
    
    // Update current index
    this.currentIndex = index;

    // Perform sliding animation
    this.performSlideAnimation(direction);

    // Update navigation buttons and progress bar
    this.updateNavigation();

    // Trigger custom event
    this.carousel.dispatchEvent(new CustomEvent('slideChange', {
      detail: { 
        currentIndex: this.currentIndex,
        totalSlides: this.totalSlides 
      }
    }));
  }

  private initializeImages(): void {
    if (this.totalSlides <= 1) return;
    
    // Load all three images: prev, current, next
    this.updateAllImages();
  }

  private updateAllImages(): void {
    const prevIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
    const nextIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;

    // Update prev image
    if (this.carouselData[prevIndex]?.main_image?.url) {
      this.prevSlide.innerHTML = this.generatePictureHTML(this.carouselData[prevIndex].main_image.url);
    }

    // Update next image 
    if (this.carouselData[nextIndex]?.main_image?.url) {
      this.nextSlide.innerHTML = this.generatePictureHTML(this.carouselData[nextIndex].main_image.url);
    }
  }

  private performSlideAnimation(direction: 'next' | 'prev'): void {
    // Add text fade animation class
    this.slide.classList.add('updating');

    // Start the slide animation
    if (direction === 'next') {
      this.imageTrack.classList.add('sliding-to-next');
    } else {
      this.imageTrack.classList.add('sliding-to-prev');
    }

    // Wait for the slide animation to complete
    setTimeout(() => {
      // Update text content
      this.updateTextContent();
      
      // Reset slide positions without animation
      this.resetSlidePositions(direction);
      
      // Re-enable animation and remove updating class
      setTimeout(() => {
        this.imageTrack.classList.remove('resetting');
        this.slide.classList.remove('updating');
        this.isAnimating = false;
        // Update navigation to re-enable buttons
        this.updateNavigation();
        // Re-measure __left height (heading may have wrapped differently)
        if (!this.isTablet) {
          requestAnimationFrame(() => this.updateFloatingImageTop());
        }
      }, 50);
    }, 600); // Match CSS animation duration
  }

  private resetSlidePositions(direction: 'next' | 'prev'): void {
    // Disable transitions temporarily
    this.imageTrack.classList.add('resetting');
    this.imageTrack.classList.remove('sliding-to-next', 'sliding-to-prev');
    
    if (direction === 'next') {
      // Move next image to current, current to prev, and load new next
      this.prevSlide.innerHTML = this.currentSlide.innerHTML;
      this.currentSlide.innerHTML = this.nextSlide.innerHTML;
      
      const nextIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;
      if (this.carouselData[nextIndex]?.main_image?.url) {
        this.nextSlide.innerHTML = this.generatePictureHTML(this.carouselData[nextIndex].main_image.url);
      }
    } else {
      // Move prev image to current, current to next, and load new prev  
      this.nextSlide.innerHTML = this.currentSlide.innerHTML;
      this.currentSlide.innerHTML = this.prevSlide.innerHTML;
      
      const prevIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
      if (this.carouselData[prevIndex]?.main_image?.url) {
        this.prevSlide.innerHTML = this.generatePictureHTML(this.carouselData[prevIndex].main_image.url);
      }
    }
  }

  private updateTextContent(): void {
    if (!this.carouselData[this.currentIndex]) return;
    
    const currentData = this.carouselData[this.currentIndex];
    
    // Update all text fields including left section heading/preheading
    const fieldsToUpdate = [
      'preheading',
      'heading',
      'right_heading',
      'text_content',
      'author_name',
      'author_title'
    ];

    fieldsToUpdate.forEach(field => {
      const element = this.slide.querySelector(`[data-field="${field}"]`) as HTMLElement;
      if (element && currentData[field]) {
        // Apply uppercase for preheading and heading to match the initial display
        if (field === 'preheading' || field === 'heading') {
          element.textContent = currentData[field].toUpperCase();
        } else {
          element.textContent = currentData[field];
        }
      }
    });

    // Update avatar
    this.updateAvatar(currentData);

    // Update floating image
    this.updateFloatingImage(currentData);
  }

  private resetLeftSectionAOS(): void {
    const leftSection = this.slide.querySelector(SELECTOR_LEFT) as HTMLElement;
    if (!leftSection) return;

    const elementsWithAOS = leftSection.querySelectorAll<HTMLElement>('[data-aos]');
    elementsWithAOS.forEach(element => {
      element.classList.remove('aos-animate', 'aos-init');
      element.style.transitionDelay = '';
      element.style.animationDelay = '';
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        AOS.refreshHard();
      });
    });
  }

  private goToPrevSlide(): void {
    if (this.isAnimating) return;
    const newIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
    this.goToSlide(newIndex, 'prev');
  }

  private goToNextSlide(): void {
    if (this.isAnimating) return;
    const newIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;
    this.goToSlide(newIndex, 'next');
  }

  private updateNavigation(): void {
    // Update progress bar
    if (this.progressBar && this.totalSlides > 0) {
      const progress = ((this.currentIndex + 1) / this.totalSlides) * 100;
      this.progressBar.style.width = `${progress}%`;
    }

    // Disable buttons during animation, otherwise enable them
    if (this.prevButton) {
      this.prevButton.disabled = this.isAnimating;
    }
    
    if (this.nextButton) {
      this.nextButton.disabled = this.isAnimating;
    }
  }


  private updateAvatar(currentData: any): void {
    const avatarContainer = this.slide.querySelector('.our-process-carousel__author-avatar') as HTMLElement;
    if (!avatarContainer) return;

    if (currentData.artisan_image && currentData.artisan_image.url) {
      // Use the preprocessed URL from Liquid template
      const avatarUrl = currentData.artisan_image.url;
      const avatarHTML = this.generateAvatarHTML(avatarUrl);
      avatarContainer.innerHTML = avatarHTML;
    } else {
      avatarContainer.innerHTML = this.getAvatarPlaceholder();
    }
  }

  private updateFloatingImage(currentData: any): void {
    const floatingImageContainer = this.slide.querySelector('.our-process-carousel__floating-image') as HTMLElement;
    if (!floatingImageContainer) return;

    if (currentData.floating_image && currentData.floating_image.url) {
      // Use the preprocessed URL from Liquid template
      const floatingImageUrl = currentData.floating_image.url;
      const floatingImageHTML = this.generateFloatingImageHTML(floatingImageUrl);
      floatingImageContainer.innerHTML = floatingImageHTML;
    } else {
      // Hide the floating image if no image data
      floatingImageContainer.innerHTML = '';
    }
  }

  private generateFloatingImageHTML(imageUrl: string): string {
    if (!imageUrl) return '';
    
    // Generate responsive picture element for floating image
    return `
      <picture>
        <img class="our-process-carousel__floating-image-img" 
             src="${imageUrl}" 
             alt="Floating Process Image" 
             width="300"
             height="auto"
             loading="lazy">
      </picture>
    `;
  }


  private generateAvatarHTML(imageUrl: string): string {
    if (!imageUrl) return this.getAvatarPlaceholder();
    
    // ImageURL is now a direct URL from Liquid preprocessing
    return `
      <picture>
        <img class="our-process-carousel__avatar-image" 
             src="${imageUrl}" 
             alt="Avatar" 
             width="60"
             height="60"
             loading="lazy">
      </picture>
    `;
  }

  private getAvatarPlaceholder(): string {
    return `
      <div class="our-process-carousel__avatar-placeholder">
        <svg class="our-process-carousel__avatar-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
      </div>
    `;
  }

  private generatePictureHTML(mediaUrl: string): string {
    if (!mediaUrl) return '';
    
    // Check if it's a video file
    if (mediaUrl.includes('.mp4')) {
      return `
        <video class="our-process-carousel__video"
               autoplay="autoplay"
               playsinline
               loop
               muted="muted"
               onclick="this.paused ? this.play() : this.pause()">
          <source src="${mediaUrl}" type="video/mp4">
        </video>
      `;
    } else {
      // Generate simple responsive picture element - mediaUrl is preprocessed from Liquid
      return `
        <picture>
          <img class="our-process-carousel__image" 
               src="${mediaUrl}" 
               alt="Our Process Image" 
               width="400"
               height="auto"
               loading="lazy">
        </picture>
      `;
    }
  }

  // Optional: Auto-play functionality
  private startAutoPlay(interval: number = 5000): void {
    setInterval(() => {
      this.goToNextSlide();
    }, interval);
  }

  // Public methods
  public getCurrentIndex(): number {
    return this.currentIndex;
  }

  public getTotalSlides(): number {
    return this.totalSlides;
  }

  public getCurrentData(): any {
    return this.carouselData[this.currentIndex];
  }

  public destroy(): void {
    // Clean up event listeners if needed
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', () => this.goToPrevSlide());
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', () => this.goToNextSlide());
    }
  }
}

// Initialize carousel when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.our-process-carousel');
  
  carousels.forEach((carousel) => {
    new OurProcessCarousel(carousel as HTMLElement);
  });
});
